import { FC, useCallback, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CircleLoader, InputText, TextError } from 'src/components';
import { SText } from 'src/components/SText';
import { FormsErrorParser } from 'src/containers/errors';
import { IdModel } from 'src/types';
import { TablePrintContent } from '../TablePrintContent';
import { PrintOnDemandFormInput } from './printOnDemand.types';
import { usePrintOnDemandForm } from './usePrintOnDemandForm';
import { usePrintOnDemandSearch } from './usePrintOnDemandSearch';

export type Props = {
  printerIsReady: boolean;
  onPrint: (doc: string, id: IdModel) => void;
  lastPrinted: IdModel | null;
};

export const PrinterOnDemand: FC<Props> = ({ printerIsReady, onPrint, lastPrinted }) => {
  const { t } = useTranslation();
  const { control, errors, handleSubmit, resetForm } = usePrintOnDemandForm();
  const { isLoading, error: searchError, data, getPrintFromSearch, resetData } = usePrintOnDemandSearch();

  const onSubmit = useCallback(
    ({ trackingId }: PrintOnDemandFormInput) => {
      getPrintFromSearch(trackingId);
    },
    [getPrintFromSearch],
  );

  useEffect(() => {
    if (lastPrinted) {
      resetData();
      resetForm({ trackingId: '' });
    }
  }, [lastPrinted, resetData, resetForm]);

  return (
    <section className="mt-3">
      <div className="my-3 m-auto ">
        <span className="text-xl text-gray-600 font-light block m-auto text-center">
          {t('printer.print-on-demand-title')}
        </span>
      </div>
      {isLoading ? (
        <CircleLoader />
      ) : (
        <div>
          <form className="m-auto" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-row  items-center m-auto justify-center">
              <SText label="printer.search-label" classNames="text-md text-gray-600 mx-2" />
              <div className="w-1/3 ml-3">
                <Controller
                  control={control}
                  name="trackingId"
                  render={({ field: { onBlur, onChange, value } }) => {
                    return (
                      <InputText
                        value={value}
                        placeholder={t('printer.tracking-search')}
                        extraClass="bg-white shadow-xl"
                        inputClass="color-primary"
                        onChange={onChange}
                        onBlur={onBlur}
                        textStyle={{
                          color: '#0085FF',
                          fontSize: '16px',
                          fontWeight: 600,
                        }}
                      />
                    );
                  }}
                />
              </div>
              <input
                type="submit"
                className="py-3 ml-2 bg-dark px-2 text-white  flex rounded-md cursor-pointer hover:bg-secondary"
                value={t('global.search') as string}
              />
            </div>
            <div className="m-auto ">
              <FormsErrorParser error={errors?.trackingId} />
            </div>
          </form>
        </div>
      )}
      {!!searchError && <TextError message={searchError.message} className="my-3  m-auto" />}

      {!isLoading && data.length > 0 && (
        <div className="w-full mb-8 overflow-hidden rounded-lg  mt-3">
          <SText
            label="printer.your-search-result"
            classNames="text-xl text-center text-gray-600 block my-6 md:my-10 font-semibold"
          />
          <TablePrintContent
            data={data}
            printerIsReady={printerIsReady}
            onPrint={onPrint}
            keysPrefix="print-on-demand"
          />
        </div>
      )}
    </section>
  );
};
